import { type RefObject, useEffect, useRef } from 'react'

/** Cierra un dropdown al clickear/tocar afuera o al apretar Escape.
 *
 * `onOutside` se guarda en un ref y no entra en las dependencias del
 * efecto: así el listener se suscribe una sola vez por apertura (cuando
 * `active` pasa a true) y no en cada render, sin pedirle a quien llama que
 * memoice el callback.
 */
export function useClickOutside(
  ref: RefObject<HTMLElement | null>,
  onOutside: () => void,
  active = true,
): void {
  const onOutsideRef = useRef(onOutside)
  onOutsideRef.current = onOutside

  useEffect(() => {
    if (!active) return

    function handlePointerDown(event: PointerEvent) {
      if (!ref.current || ref.current.contains(event.target as Node)) return
      // Un click adentro de OTRO modal (uno anidado y montado encima, como
      // StepUpDialog sobre el formulario que lo abrió) no cuenta como
      // "afuera" de ÉSTE — son árboles de DOM hermanos, no anidados, así
      // que ese click cae técnicamente fuera de `ref` igual. Sin este
      // chequeo, escribir la contraseña en el popup de arriba cierra el
      // modal de abajo solo. `[aria-modal="true"]` es el marcador común de
      // Modal.tsx y ConfirmDialog.tsx.
      //
      // Pero sólo cuenta cuando ese modal encontrado es OTRO, no el mismo en
      // el que `ref` ya vive: un dropdown propio (los MultiSelectPicker de
      // LaunchRunForm, por ejemplo) tiene como ancestro más cercano con
      // aria-modal al Modal.tsx que lo contiene a ÉL TAMBIÉN — sin este
      // segundo chequeo, clickear el gatillo de un dropdown hermano quedaba
      // "adentro de un modal" y nunca cerraba al primero.
      const target = event.target as Element | null
      const nestedModal = target?.closest('[aria-modal="true"]')
      if (nestedModal && !nestedModal.contains(ref.current)) return
      onOutsideRef.current()
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      // Mismo motivo que arriba, pero sólo entre modales de verdad: un
      // dropdown (UserMenu, los pickers de DataTable) nunca lleva
      // aria-modal, así que Escape lo sigue cerrando igual que siempre,
      // sin importar si hay algún modal abierto en otro lado de la
      // pantalla. Entre modales sí importa: si hay uno más nuevo montado
      // encima, Escape lo tiene que cerrar a ÉSE (que escucha lo mismo y
      // ya se encarga), no en cascada a los de abajo también.
      if (ref.current?.getAttribute('aria-modal') === 'true') {
        const dialogs = document.querySelectorAll('[aria-modal="true"]')
        if (dialogs[dialogs.length - 1] !== ref.current) return
      }
      onOutsideRef.current()
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [ref, active])
}
